import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Slider,
  Button,
  Chip,
  Stack,
  Divider,
  IconButton,
  Collapse,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { GraphFilterOptions } from '../../../types/graph.types';

interface FilterPreset {
  id: string;
  name: string;
  filters: GraphFilterOptions;
  createdAt: Date;
}

interface GraphFilterPanelProps {
  open: boolean;
  onClose: () => void;
  currentFilters: GraphFilterOptions;
  onApplyFilters: (filters: GraphFilterOptions) => void;
  onClearFilters: () => void;
}

/**
 * GraphFilterPanel Component
 *
 * Advanced filtering panel for the knowledge graph visualization.
 * Allows users to filter by date range, keywords, node degree, and combine filters with AND/OR logic.
 * Supports saving and loading filter presets for quick access to common filter combinations.
 *
 * Features:
 * - Date range filtering (start and end dates)
 * - Keyword search across node labels and content
 * - Minimum node degree filtering (show only well-connected nodes)
 * - Filter preset management (save, load, delete)
 * - Collapsible sections for better organization
 * - Real-time filter preview
 */
const GraphFilterPanel: React.FC<GraphFilterPanelProps> = ({
  open,
  onClose,
  currentFilters,
  onApplyFilters,
  onClearFilters,
}) => {
  // Local filter state (applied when user clicks "Apply Filters")
  const [dateStart, setDateStart] = useState<Date | null>(
    currentFilters.dateRange?.start || null
  );
  const [dateEnd, setDateEnd] = useState<Date | null>(
    currentFilters.dateRange?.end || null
  );
  const [keywordSearch, setKeywordSearch] = useState<string>(
    currentFilters.searchTerm || ''
  );
  const [minDegree, setMinDegree] = useState<number>(
    currentFilters.minNodeDegree || 0
  );

  // Preset management
  const [presets, setPresets] = useState<FilterPreset[]>(() => {
    // Load presets from localStorage
    const savedPresets = localStorage.getItem('graphFilterPresets');
    return savedPresets ? JSON.parse(savedPresets) : [];
  });
  const [presetName, setPresetName] = useState<string>('');
  const [showPresetForm, setShowPresetForm] = useState<boolean>(false);

  // Section expansion state
  const [dateExpanded, setDateExpanded] = useState<boolean>(true);
  const [keywordExpanded, setKeywordExpanded] = useState<boolean>(true);
  const [degreeExpanded, setDegreeExpanded] = useState<boolean>(true);
  const [presetsExpanded, setPresetsExpanded] = useState<boolean>(false);
  const [edgesExpanded, setEdgesExpanded] = useState<boolean>(false);
  const featureFlags = useFeatureFlags();

  // Semantic edge visibility state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const semanticEdgeTypes: string[] = ['SUPPORTS', 'CONTRADICTS', 'CAUSES', 'DEPENDS_ON', 'RELATES_TO'];
  const [hiddenEdgeTypes, setHiddenEdgeTypes] = useState<Set<string>>(new Set());

  const toggleEdgeType = (edgeType: string) => {
    setHiddenEdgeTypes(prev => {
      const next = new Set(prev);
      if (next.has(edgeType)) {
        next.delete(edgeType);
      } else {
        next.add(edgeType);
      }
      return next;
    });
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    const filters: GraphFilterOptions = {
      ...currentFilters,
      dateRange: dateStart && dateEnd ? { start: dateStart, end: dateEnd } : undefined,
      searchTerm: keywordSearch || undefined,
      minNodeDegree: minDegree > 0 ? minDegree : undefined,
    };

    onApplyFilters(filters);
    console.log('📊 GRAPH FILTER: Applied filters', filters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setDateStart(null);
    setDateEnd(null);
    setKeywordSearch('');
    setMinDegree(0);
    onClearFilters();
  };

  // Handle save preset
  const handleSavePreset = () => {
    if (!presetName.trim()) {
      return;
    }

    const newPreset: FilterPreset = {
      id: Date.now().toString(),
      name: presetName,
      filters: {
        dateRange: dateStart && dateEnd ? { start: dateStart, end: dateEnd } : undefined,
        searchTerm: keywordSearch || undefined,
        minNodeDegree: minDegree > 0 ? minDegree : undefined,
      },
      createdAt: new Date(),
    };

    const updatedPresets = [...presets, newPreset];
    setPresets(updatedPresets);
    localStorage.setItem('graphFilterPresets', JSON.stringify(updatedPresets));

    setPresetName('');
    setShowPresetForm(false);
    console.log('📊 GRAPH FILTER: Saved preset', newPreset);
  };

  // Handle load preset
  const handleLoadPreset = (preset: FilterPreset) => {
    setDateStart(preset.filters.dateRange?.start || null);
    setDateEnd(preset.filters.dateRange?.end || null);
    setKeywordSearch(preset.filters.searchTerm || '');
    setMinDegree(preset.filters.minNodeDegree || 0);

    // Auto-apply when loading preset
    onApplyFilters(preset.filters);
    console.log('📊 GRAPH FILTER: Loaded preset', preset);
  };

  // Handle delete preset
  const handleDeletePreset = (presetId: string) => {
    const updatedPresets = presets.filter(p => p.id !== presetId);
    setPresets(updatedPresets);
    localStorage.setItem('graphFilterPresets', JSON.stringify(updatedPresets));
    console.log('📊 GRAPH FILTER: Deleted preset', presetId);
  };

  // Count active filters
  const activeFilterCount = [
    dateStart && dateEnd,
    keywordSearch,
    minDegree > 0,
  ].filter(Boolean).length;

  if (!open) return null;

  return (
    <Paper
      elevation={3}
      style={{
        position: 'absolute',
        top: 16,
        right: 16,
        width: 360,
        maxHeight: 'calc(100vh - 200px)',
        overflow: 'auto',
        zIndex: 1000,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          backgroundColor: '#325BA8',
          color: '#fff',
        }}
      >
        <FilterListIcon style={{ marginRight: 8 }} />
        <Typography variant="h6" style={{ flexGrow: 1 }}>
          Advanced Filters
        </Typography>
        {activeFilterCount > 0 && (
          <Chip
            label={`${activeFilterCount} active`}
            size="small"
            style={{ marginRight: 8, backgroundColor: 'rgba(255,255,255,0.2)' }}
          />
        )}
        <IconButton size="small" onClick={onClose} style={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </div>

      {/* Content */}
      <div style={{ padding: 16 }}>
        {/* Date Range Filter */}
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              marginBottom: 8,
            }}
            onClick={() => setDateExpanded(!dateExpanded)}
          >
            <Typography variant="subtitle2" style={{ flexGrow: 1, fontWeight: 600 }}>
              Date Range
            </Typography>
            <IconButton size="small">
              {dateExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>

          <Collapse in={dateExpanded}>
            <Stack spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                fullWidth
                value={dateStart ? dateStart.toISOString().split('T')[0] : ''}
                onChange={(e) => setDateStart(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                fullWidth
                value={dateEnd ? dateEnd.toISOString().split('T')[0] : ''}
                onChange={(e) => setDateEnd(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Stack>
          </Collapse>
        </div>

        <Divider style={{ marginTop: 16, marginBottom: 16 }} />

        {/* Keyword Search Filter */}
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              marginBottom: 8,
            }}
            onClick={() => setKeywordExpanded(!keywordExpanded)}
          >
            <Typography variant="subtitle2" style={{ flexGrow: 1, fontWeight: 600 }}>
              Keyword Search
            </Typography>
            <IconButton size="small">
              {keywordExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>

          <Collapse in={keywordExpanded}>
            <TextField
              fullWidth
              size="small"
              label="Search nodes"
              placeholder="Enter keywords..."
              value={keywordSearch}
              onChange={(e) => setKeywordSearch(e.target.value)}
              helperText="Search across node labels and content"
            />
          </Collapse>
        </div>

        <Divider style={{ marginTop: 16, marginBottom: 16 }} />

        {/* Node Degree Filter */}
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              marginBottom: 8,
            }}
            onClick={() => setDegreeExpanded(!degreeExpanded)}
          >
            <Typography variant="subtitle2" style={{ flexGrow: 1, fontWeight: 600 }}>
              Minimum Connections
            </Typography>
            <IconButton size="small">
              {degreeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>

          <Collapse in={degreeExpanded}>
            <div style={{ paddingLeft: 8, paddingRight: 8 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Show only nodes with at least {minDegree} connection{minDegree !== 1 ? 's' : ''}
              </Typography>
              <Slider
                value={minDegree}
                onChange={(_, value) => setMinDegree(value as number)}
                min={0}
                max={20}
                step={1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                ]}
                valueLabelDisplay="auto"
              />
            </div>
          </Collapse>
        </div>

        {/* Semantic Edge Types Filter */}
        {featureFlags.semanticRelationships && (
          <>
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  marginBottom: 8,
                }}
                onClick={() => setEdgesExpanded(!edgesExpanded)}
              >
                <Typography variant="subtitle2" style={{ flexGrow: 1, fontWeight: 600 }}>
                  Semantic Edges
                </Typography>
                <IconButton size="small">
                  {edgesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </div>
              <Collapse in={edgesExpanded}>
                <Stack spacing={0.5}>
                  {[
                    { type: 'SUPPORTS', label: 'Supports', color: '#4DC4D2' },
                    { type: 'CONTRADICTS', label: 'Contradicts', color: '#E94257' },
                    { type: 'CAUSES', label: 'Causes', color: '#325BA8' },
                    { type: 'DEPENDS_ON', label: 'Depends On', color: '#892987' },
                    { type: 'RELATES_TO', label: 'Relates To', color: '#999999' },
                  ].map(({ type, label, color }) => (
                    <div
                      key={type}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        paddingLeft: 8,
                        paddingRight: 8,
                        paddingTop: 4,
                        paddingBottom: 4,
                        borderRadius: 4,
                        opacity: hiddenEdgeTypes.has(type) ? 0.4 : 1,
                      }}
                      onClick={() => toggleEdgeType(type)}
                    >
                      <div
                        style={{
                          width: 20,
                          height: 3,
                          backgroundColor: color,
                          marginRight: 8,
                          borderStyle: type === 'CONTRADICTS' ? 'dashed' : type === 'DEPENDS_ON' ? 'dotted' : 'solid',
                        }}
                      />
                      <Typography variant="body2" style={{ flex: 1 }}>
                        {label}
                      </Typography>
                      <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                        {hiddenEdgeTypes.has(type) ? 'Hidden' : 'Visible'}
                      </Typography>
                    </div>
                  ))}
                </Stack>
              </Collapse>
            </div>
          </>
        )}

        <Divider style={{ marginTop: 16, marginBottom: 16 }} />

        {/* Filter Presets */}
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              marginBottom: 8,
            }}
            onClick={() => setPresetsExpanded(!presetsExpanded)}
          >
            <Typography variant="subtitle2" style={{ flexGrow: 1, fontWeight: 600 }}>
              Filter Presets
            </Typography>
            <Chip
              label={presets.length}
              size="small"
              style={{ marginRight: 8 }}
            />
            <IconButton size="small">
              {presetsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>

          <Collapse in={presetsExpanded}>
            <Stack spacing={1}>
              {/* Save New Preset */}
              {!showPresetForm && (
                <Button
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={() => setShowPresetForm(true)}
                  disabled={activeFilterCount === 0}
                >
                  Save Current Filters
                </Button>
              )}

              {showPresetForm && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Preset Name"
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    autoFocus
                  />
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSavePreset}
                    disabled={!presetName.trim()}
                  >
                    Save
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setShowPresetForm(false);
                      setPresetName('');
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              )}

              {/* Preset List */}
              {presets.length === 0 && !showPresetForm && (
                <Alert severity="info" style={{ marginTop: 8 }}>
                  No saved presets. Apply some filters and save them for quick access.
                </Alert>
              )}

              {presets.map((preset) => (
                <Paper
                  key={preset.id}
                  variant="outlined"
                  style={{
                    padding: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div style={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {preset.name}
                    </Typography>
                    <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }}>
                      {preset.filters.dateRange && 'Date range • '}
                      {preset.filters.searchTerm && 'Keyword • '}
                      {preset.filters.minNodeDegree && `Min degree: ${preset.filters.minNodeDegree}`}
                    </Typography>
                  </div>
                  <Tooltip title="Load preset">
                    <IconButton
                      size="small"
                      onClick={() => handleLoadPreset(preset)}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete preset">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeletePreset(preset.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>
              ))}
            </Stack>
          </Collapse>
        </div>

        <Divider style={{ marginTop: 16, marginBottom: 16 }} />

        {/* Action Buttons */}
        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleApplyFilters}
            disabled={activeFilterCount === 0}
          >
            Apply Filters
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClearFilters}
            disabled={activeFilterCount === 0}
          >
            Clear All
          </Button>
        </Stack>

        {/* Active Filters Summary */}
        {activeFilterCount > 0 && (
          <div style={{ marginTop: 16 }}>
            <Typography variant="caption" style={{ color: 'rgba(0,0,0,0.6)' }} gutterBottom>
              Active Filters:
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" style={{ marginTop: 4 }}>
              {dateStart && dateEnd && (
                <Chip
                  label={`${dateStart.toLocaleDateString()} - ${dateEnd.toLocaleDateString()}`}
                  size="small"
                  onDelete={() => {
                    setDateStart(null);
                    setDateEnd(null);
                  }}
                />
              )}
              {keywordSearch && (
                <Chip
                  label={`"${keywordSearch}"`}
                  size="small"
                  onDelete={() => setKeywordSearch('')}
                />
              )}
              {minDegree > 0 && (
                <Chip
                  label={`≥${minDegree} connections`}
                  size="small"
                  onDelete={() => setMinDegree(0)}
                />
              )}
            </Stack>
          </div>
        )}
      </div>
    </Paper>
  );
};

export default GraphFilterPanel;
